import { createFileRoute } from "@tanstack/react-router";
import { useAllSites } from "@/api/auth/sites";
import { AdminPageHeader } from "@/components/admin/admin-page-header";
import { SitesCard } from "@/components/organisations/sites-card";
import { Skeleton } from "@/components/ui/skeleton";
import { requireMinRole } from "@/lib/route-guards";

export const Route = createFileRoute("/dashboard/_admin/sites")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    // Site management is platform-admin only; non-admins get the explicit
    // "not authorised" page rather than a silent bounce (CTE-127).
    requireMinRole(context.session, "admin", "/no-access");
  },
  staticData: {
    title: "Asset Management",
  },
});

function RouteComponent() {
  const { data: sitesData, isLoading, error } = useAllSites();

  if (error) {
    return (
      <div className="p-4 lg:p-7">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-destructive font-semibold">
            Error loading sites: {error.message}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 lg:p-7 space-y-6">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Group sites by organization
  const sitesByOrg = (sitesData || []).reduce(
    (acc, site) => {
      const orgId = site.organizationId;
      const orgName = site.organization?.name || "Unknown Organization";

      if (!acc[orgId]) {
        acc[orgId] = {
          name: orgName,
          sites: [],
        };
      }
      acc[orgId].sites.push(site);
      return acc;
    },
    {} as Record<
      string,
      { name: string; sites: NonNullable<typeof sitesData> }
    >,
  );

  const orgEntries = Object.entries(sitesByOrg);

  return (
    <div className="p-4 lg:p-7 space-y-6">
      <AdminPageHeader
        title="Asset Management"
        description={
          <>
            Every asset across every organization on the platform. Use{" "}
            <strong className="text-white">Your Assets</strong> to see only the
            assets you're a member of.
          </>
        }
      />

      {orgEntries.length === 0 ? (
        <SitesCard
          title="Assets"
          description="Manage all assets across your organizations."
          sites={[]}
          isLoading={false}
          emptyMessage="There are no assets configured yet."
        />
      ) : (
        orgEntries.map(([orgId, { name, sites }]) => (
          <SitesCard
            key={orgId}
            title={name}
            description={`Manage assets for ${name}.`}
            sites={sites}
            isLoading={false}
          />
        ))
      )}
    </div>
  );
}
